import { useState, useCallback, useEffect, useMemo } from 'react'
import { useScreen, mobile } from '../../../../utils/responsive-helper'
import { useTranslation } from 'react-i18next'
import { FONT_BLACK, FONT_LIGHT_GRAY, INPUT_ERROR } from '../../../../constant/colors'
import { reduxForm, InjectedFormProps, getFormValues } from 'redux-form'
import { useGetUserCoin } from '../../../../services/user/user-query'
import { numberWithCommas, useRouter } from '../../../../utils/helper'
import { useSelector } from 'react-redux'
import { useVisibility } from '../../../../utils/custom-hook'
import { useHistory } from 'react-router'
import { enrollmentWellBeing } from '../../../../constant/path'
import { pullAt } from 'lodash'
import {
  useCreateUserClaimEnrollment,
  useGetEClaimEnrollment,
} from '../../../../services/e-claim/e-claim-query'
import {
  CreateUserClaimEnrollments,
  EClaimEnllomentItem,
  PreSelectedClaimWrapperList,
} from '../../../../services/e-claim/e-claim-types'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import Breadcrumb from '../../../../components/common/Breadcrumb'
import Text from '../../../../components/common/Text'
import Card from '../../../../components/common/Card'
import Button from '../../../../components/common/Button'
import FooterContent from '../../../../components/FooterContent'
import Modal from '../../../../components/common/Modal'
import Loading from '../../../../components/common/Loading'
import ChoicesRadio from './ChoicesRadio'
import { useGetEnrollmentYear } from '../..'

const Layout = styled.div`
  margin-bottom: 40px;
`

const BreadcrumbLayout = styled.div`
  margin-top: 24px;
`

const Row = styled.div`
  display: flex;
  flex-flow: row;
`
const Column = styled.div`
  display: flex;
  flex-flow: column;
`

const RowPaddingBottom = styled(Row)`
  margin-bottom: 24px;
`

const LayoutTitle = styled(Row)`
  justify-content: center;
  text-align: center;
  margin-top: 24px;
  margin-bottom: 27px;
`

const LayoutCard = styled(Card)`
  padding-top: 12px;
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 24px;
  margin-bottom: 24px;
  ${mobile} {
    padding: 8px;
  }
  .icon-enroll {
    margin-right: 16px;
  }
`

const TextModal = styled(Text)`
  margin: 30px;
`

const ButtonStyle = styled(Button)`
  width: 267px;
`

const string2Number = (value: string) => {
  return Number(value.replaceAll(',', ''))
}

const EDIT_SELF_BENEFITS_FORM_NAME = 'EditSelfBenefits'
const enchancer = reduxForm<{ [key: string]: any }>({
  form: EDIT_SELF_BENEFITS_FORM_NAME,
})
type EditWellBeingFormProps = InjectedFormProps<{ [key: string]: any }>

const EditWellBeing = (props: EditWellBeingFormProps) => {
  const { handleSubmit, initialize } = props

  const { query } = useRouter()
  const history = useHistory()
  const claimTypeId = query?.claimTypeId!
  const benefitYearId = query?.benefitYearId!

  const { t } = useTranslation()
  const { isMobile } = useScreen()
  const { enrollmentYear } = useGetEnrollmentYear()
  const { data: coinDefault } = useGetUserCoin()
  const { data: eClaimEnllomentItems, isLoading } = useGetEClaimEnrollment(benefitYearId)

  const [visible, modalAction] = useVisibility()
  const [errorMessage, setErrorMessage] = useState('')
  const { mutate: createUserClaimEnrollment, isLoading: isSubmitting } =
    useCreateUserClaimEnrollment()

  const formValuesSelector = getFormValues(EDIT_SELF_BENEFITS_FORM_NAME)
  const formValues = useSelector<any, { [key: string]: any }>((state) => formValuesSelector(state))

  const [title, setTitle] = useState('')
  const [options, setOptions] = useState<PreSelectedClaimWrapperList[]>([])

  const [valueRadio, setValuetRadio] = useState<Number[]>([])
  const [actuallyCoin, setActuallyCoin] = useState(coinDefault || 0)
  const [useCoin, setUseCoin] = useState(0)

  const sumPriceFromOption = (defaultValue: number) =>
    options
      .filter((option) => option.preSelectedAmount != null)
      .reduce((sum, option2) => (sum += option2.preSelectedAmount || 0), defaultValue)

  useEffect(() => {
    let options: any[] = []
    eClaimEnllomentItems?.forEach((eClaimEnllomentItem: EClaimEnllomentItem) => {
      const isSelectClaimTypeId = Number(claimTypeId) === Number(eClaimEnllomentItem.claimTypeId)
      if (isSelectClaimTypeId) {
        setTitle(eClaimEnllomentItem.claimTypeName)
        setOptions(eClaimEnllomentItem.preSelectedClaimWrapperList)
        options = eClaimEnllomentItem.preSelectedClaimWrapperList
      }
    })

    const initialValues: Partial<{ [key: string]: any }> = {}
    options?.forEach((option) => {
      const { claimId, preSelectedAmount } = option
      initialValues[`point_${claimId?.toString()}`] =
        preSelectedAmount === null ? 0 : Number(preSelectedAmount)
    })
    setValuetRadio(
      options.filter((option) => option.preSelectedAmount != null).map((option) => option.claimId),
    )
    setActuallyCoin(sumPriceFromOption(coinDefault || 0))
    setUseCoin(sumPriceFromOption(0))
    initialize(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eClaimEnllomentItems, claimTypeId, benefitYearId])

  const onSelectRadio = useCallback(
    (value: number) => {
      const index = valueRadio.indexOf(value)
      if (index !== -1) {
        valueRadio.splice(index, 1)
        setValuetRadio([...valueRadio])
      } else {
        setValuetRadio([...valueRadio, value])
      }
    },
    [valueRadio],
  )

  useEffect(() => {
    let sumNewValue = 0
    if (formValues) {
      Object.entries(formValues).forEach(([key, v]) => {
        const targetId = Number(key.split('_')[1])
        const isSeleted = valueRadio.includes(targetId)

        const value = string2Number(`${v}`)
        const isNumberic = !isNaN(value)

        if (isSeleted && isNumberic) {
          sumNewValue += Number(value)
        }
      })
    }

    setUseCoin(sumNewValue)
    setActuallyCoin(Number(sumPriceFromOption(coinDefault || 0)) - Number(sumNewValue))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, coinDefault, valueRadio])

  const canSubmit = useMemo(() => {
    if (actuallyCoin >= 0 && !isSubmitting) {
      return true
    } else {
      return false
    }
  }, [actuallyCoin, isSubmitting])

  const onSubmit = useCallback(
    (value: any) => {
      const usersPreSelectedClaimsDTOList: any[] = []
      Object.entries(value).forEach(([key, value]) => {
        const targetId = Number(key.split('_')[1])

        if (valueRadio.includes(targetId)) {
          const preSelectedAmount = string2Number(String(value))
          const claimId = String(targetId)
          usersPreSelectedClaimsDTOList.push({ preSelectedAmount, claimId })
        }
      })

      const params: CreateUserClaimEnrollments = [
        { claimTypeId, benefitYearId, usersPreSelectedClaimsDTOList },
      ]
      createUserClaimEnrollment(params, {
        onSuccess: () => {
          history.push(enrollmentWellBeing({ routeParam: { benefitYearId } }))
        },
        onError: (error) => {
          setErrorMessage(error.message)
          modalAction.show()
        },
      })
    },
    [benefitYearId, claimTypeId, createUserClaimEnrollment, history, modalAction, valueRadio],
  )

  return (
    <>
      <Modal
        visible={visible}
        onCancel={() => {
          modalAction.hide()
        }}
        hideFooter
      >
        <TextModal style={{ margin: '30px' }}>{errorMessage}</TextModal>
      </Modal>
      <Layout>
        {!isMobile && (
          <BreadcrumbLayout>
            <Breadcrumb
              overide={(paths) => {
                // paths.unshift({ label: 'Home', path: '/' })
                paths[0].label = t('ปรับเปลี่ยนสวัสดิการ')
                paths[1].label = t(`ดูสวัสดิการปี ${enrollmentYear}`)
                paths[2].label = t(`สวัสดิการปี ${enrollmentYear} ของฉัน`)
                paths[3].label = 'สวัสดิการเพิ่มเติม'
                paths[4].label = title
                pullAt(paths, 5)
                return paths
              }}
            />
          </BreadcrumbLayout>
        )}
        <LayoutTitle>
          <Text size={28} type="Bold" color={FONT_BLACK}>
            สวัสดิการเพิ่มเติม
          </Text>
        </LayoutTitle>
        <RowPaddingBottom>
          <Text size={22} type="Bold" color={FONT_BLACK}>
            เลือกสวัสดิการที่ต้องการเบิก
          </Text>
        </RowPaddingBottom>
        <LayoutCard>
          <Loading loading={isLoading}>
            <RowPaddingBottom>
              <Text size={18} type={'SemiBold'} color={FONT_BLACK}>
                {title}
              </Text>
            </RowPaddingBottom>

            <ChoicesRadio value={valueRadio} options={options} onChange={onSelectRadio} />
          </Loading>
        </LayoutCard>
        <FooterContent
          style={{
            height: 80,
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
          }}
        >
          <Column>
            <Row>
              <Text size={18} type={'Bold'}>
                รวมใช้คะแนน
              </Text>
            </Row>
            <Row>
              <Loading loading={isLoading}>
                <Text color={INPUT_ERROR} size={18} type={'SemiBold'}>
                  {numberWithCommas(useCoin * -1)}&nbsp;
                </Text>
                <Text size={18} type={'SemiBold'}>
                  คะแนน&nbsp;
                </Text>
                <Text color={FONT_LIGHT_GRAY} size={18} type={'SemiBold'}>
                  (คงเหลือ {numberWithCommas(actuallyCoin || 0)} คะแนน)
                </Text>
              </Loading>
            </Row>
          </Column>
          <ButtonStyle onClick={handleSubmit(onSubmit)} disabled={!canSubmit}>
            เลือกสวัสดิการเพิ่มเติม
          </ButtonStyle>
        </FooterContent>
      </Layout>
    </>
  )
}

export default enchancer(EditWellBeing)
